import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/routes/protected-route";
import { SectionGuard } from "@/routes/section-guard";
import LoginPage from "@/routes/login";
import ChangePasswordPage from "@/routes/change-password";
import DashboardLayout from "@/routes/dashboard/layout";
import DashboardHome from "@/routes/dashboard";
import AdminSettingsPage from "@/routes/dashboard/admin-settings";
import { PlaceholderPage } from "@/routes/dashboard/placeholder";
import UserManagementPage from "@/routes/dashboard/users";
import NewSiteUserPage from "@/routes/dashboard/users/new";
import B2bIndex from "@/routes/dashboard/b2b/index";
import B2bExternalApi from "@/routes/dashboard/b2b/external-api/index";
import B2bClientsList from "@/routes/dashboard/b2b/clients/index";
import B2bClientDetail from "@/routes/dashboard/b2b/clients/detail";
import B2bNewClient from "@/routes/dashboard/b2b/clients/new";
import B2bCatalog from "@/routes/dashboard/b2b/catalog/index";
import B2bWidgets from "@/routes/dashboard/b2b/widgets/index";
import B2bEndpointEditor from "@/routes/dashboard/b2b/catalog/editor";
import B2bChanges from "@/routes/dashboard/b2b/changes/index";
import B2bUsage from "@/routes/dashboard/b2b/usage/index";
import B2bApiUsage from "@/routes/dashboard/b2b/api-usage/index";
import B2bSettings from "@/routes/dashboard/b2b/settings";
import ILWebsiteIndex from "@/routes/dashboard/il-website";
import ArticlesList from "@/routes/dashboard/il-website/articles-list";
import ArticleEditor from "@/routes/dashboard/il-website/article-editor";
import TranslationsList from "@/routes/dashboard/translations";
import TranslationEditor from "@/routes/dashboard/translations/editor";
import AnalystReportsPage from "@/routes/dashboard/analyst-reports";
import NewsPortalIndex from "@/routes/dashboard/news-portal";
import CommentsPage from "@/routes/dashboard/comments";
import CheckoutIndex from "@/routes/dashboard/checkout";
import CheckoutDesignLpPage from "@/routes/dashboard/checkout/design-lp";
import CheckoutDiscountPricesPage from "@/routes/dashboard/checkout/discount-prices";
import PrivateCompaniesIndex from "@/routes/dashboard/private-companies";
import PrivateCompaniesLinkedInPage from "@/routes/dashboard/private-companies/linkedin";
import AssetsLogoIndex from "@/routes/dashboard/assets-logo";
import AssetsLogoUploadPage from "@/routes/dashboard/assets-logo/upload";
import AdsPage from "@/routes/dashboard/ads";
import DataIndex from "@/routes/dashboard/data";
import DataPipelinePage from "@/routes/dashboard/data/pipeline";
import DataStockEtlPage from "@/routes/dashboard/data/stock-etl";
import DataPurgePage from "@/routes/dashboard/data/purge";
import WritingQualityPage from "@/routes/dashboard/writing-quality";
import WriterQualityPage from "@/routes/dashboard/writing-quality/writer";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardHome />} />

                <Route element={<SectionGuard section="adminSettings" />}>
                  <Route path="admin-settings" element={<AdminSettingsPage />} />
                </Route>

                <Route element={<SectionGuard section="ilWebsite" />}>
                  <Route path="il-website" element={<ILWebsiteIndex />} />
                  <Route path="il-website/articles-editor" element={<ArticlesList />} />
                  <Route
                    path="il-website/articles-editor/:id"
                    element={<ArticleEditor />}
                  />
                </Route>

                <Route element={<SectionGuard section="translations" />}>
                  <Route path="translations" element={<TranslationsList />} />
                  <Route path="translations/:ticker" element={<TranslationEditor />} />
                </Route>

                {/* Placeholders — sections kept in the sidebar but not yet ported. */}
                <Route element={<SectionGuard section="payments" />}>
                  <Route path="payments" element={<PlaceholderPage section="payments" />} />
                </Route>
                <Route element={<SectionGuard section="checkout" />}>
                  <Route path="checkout" element={<CheckoutIndex />} />
                  <Route path="checkout/design-lp" element={<CheckoutDesignLpPage />} />
                  <Route
                    path="checkout/discount-prices"
                    element={<CheckoutDiscountPricesPage />}
                  />
                </Route>
                <Route element={<SectionGuard section="marketing" />}>
                  <Route path="marketing" element={<PlaceholderPage section="marketing" />} />
                </Route>
                <Route element={<SectionGuard section="userManagement" />}>
                  <Route path="users" element={<UserManagementPage />} />
                  <Route path="users/new" element={<NewSiteUserPage />} />
                </Route>
                <Route element={<SectionGuard section="newswire" />}>
                  <Route path="newswire" element={<PlaceholderPage section="newswire" />} />
                </Route>
                <Route element={<SectionGuard section="newsPortal" />}>
                  <Route path="news-portal" element={<NewsPortalIndex />} />
                  <Route path="news-portal/comments" element={<CommentsPage />} />
                </Route>
                <Route element={<SectionGuard section="b2b" />}>
                  <Route path="b2b" element={<B2bIndex />} />
                  <Route path="b2b/external-api" element={<B2bExternalApi />} />
                  <Route path="b2b/clients" element={<B2bClientsList />} />
                  <Route path="b2b/clients/new" element={<B2bNewClient />} />
                  <Route path="b2b/clients/:id" element={<B2bClientDetail />} />
                  <Route path="b2b/catalog" element={<B2bCatalog />} />
                  <Route path="b2b/catalog/new" element={<B2bEndpointEditor />} />
                  <Route path="b2b/catalog/:slug" element={<B2bEndpointEditor />} />
                  <Route path="b2b/usage" element={<B2bUsage />} />
                  <Route path="b2b/api-usage" element={<B2bApiUsage />} />
                  <Route path="b2b/changes" element={<B2bChanges />} />
                  <Route path="b2b/changes/:id" element={<B2bChanges />} />
                  <Route path="b2b/settings" element={<B2bSettings />} />
                  <Route path="b2b/widgets" element={<B2bWidgets />} />
                </Route>
                <Route element={<SectionGuard section="portfolio" />}>
                  <Route path="portfolio" element={<PlaceholderPage section="portfolio" />} />
                </Route>
                <Route element={<SectionGuard section="alertsManager" />}>
                  <Route path="alerts" element={<PlaceholderPage section="alertsManager" />} />
                </Route>
                <Route element={<SectionGuard section="riskFactors" />}>
                  <Route path="risk-factors" element={<PlaceholderPage section="riskFactors" />} />
                </Route>
                <Route element={<SectionGuard section="analystReports" />}>
                  <Route path="analyst-reports" element={<AnalystReportsPage />} />
                </Route>
                <Route element={<SectionGuard section="privateCompanies" />}>
                  <Route
                    path="private-companies"
                    element={<PrivateCompaniesIndex />}
                  />
                  <Route
                    path="private-companies/linkedin"
                    element={<PrivateCompaniesLinkedInPage />}
                  />
                </Route>
                <Route element={<SectionGuard section="assetsLogo" />}>
                  <Route path="assets-logo" element={<AssetsLogoIndex />} />
                  <Route
                    path="assets-logo/upload"
                    element={<AssetsLogoUploadPage />}
                  />
                </Route>
                <Route element={<SectionGuard section="ads" />}>
                  <Route path="ads" element={<AdsPage />} />
                </Route>
                <Route element={<SectionGuard section="data" />}>
                  <Route path="data" element={<DataIndex />} />
                  <Route path="data/pipeline" element={<DataPipelinePage />} />
                  <Route path="data/stock-etl" element={<DataStockEtlPage />} />
                  <Route path="data/purge" element={<DataPurgePage />} />
                </Route>
                <Route element={<SectionGuard section="writingQuality" />}>
                  <Route path="writing-quality" element={<WritingQualityPage />} />
                  <Route
                    path="writing-quality/writers/:author"
                    element={<WriterQualityPage />}
                  />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
